import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FileText,
  Link2,
  LockKeyhole,
  LogOut,
  Rocket,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Button, Input, Spinner } from '@helios/ui';
import { hasPermission } from '@helios/authz';
import type { CompanySetupView } from '@helios/types';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import { useAuth } from '@/features/auth/authContext';
import { useGlassSpotlight } from '@/hooks/useGlassSpotlight';
import {
  companySetupClient,
  type SetupDraft,
  type SetupInvitation,
  type SetupSummary,
} from '@/services/companySetupClient';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';
import {
  SetupCompanyForm,
  SetupConnections,
  SetupImportReview,
  SetupLaunch,
  SetupPeople,
} from './CompanySetupSections';
import { SetupCheck, SetupField } from './SetupControls';
import { SetupBlueprint } from './SetupBlueprint';
import { SetupAssistant } from './SetupAssistant';
import { CinematicWelcome } from './CinematicWelcome';
import { readOwnerInvitation, type OwnerInvitationEntry } from './ownerInvitationLink';
import './company-setup.css';
import './setup-atmosphere.css';
import './setup-cinematic.css';

function SetupBrand() {
  return (
    <Link to="/company-setup" className="company-setup-brand">
      <ConstructionOsLogo variant="toolbar" />
      <span>
        HELIOS <small>CONSTRUCTION OS</small>
      </span>
    </Link>
  );
}
const draftFrom = (view: CompanySetupView): SetupDraft => ({
  profile: view.profile,
  roster: view.roster,
  providers: view.providers,
  tasks: view.tasks,
  importMode: view.importMode,
  accessApproved: view.accessApproved,
  ...(view.rolePolicyRevision ? { rolePolicyRevision: view.rolePolicyRevision } : {}),
  sourceApproved: view.sourceApproved,
  aiConsent: view.aiConsent,
});
const STEPS = ['Company', 'People & access', 'Connections', 'Import review', 'Launch'];
const STEP_DETAILS = [
  {
    icon: Building2,
    detail: 'Define your workspace',
    description: 'Start with your company details, then choose what matters to your business.',
  },
  {
    icon: Users,
    detail: 'Bring your team together',
    description: 'Add your people and review exactly who can access your workspace.',
  },
  {
    icon: Link2,
    detail: 'Connect your tools',
    description: 'Choose your providers and connect the right company accounts.',
  },
  {
    icon: FileText,
    detail: 'Review your sources',
    description: 'Check provider identities and decide which records belong in your workspace.',
  },
  {
    icon: Rocket,
    detail: 'Review and launch',
    description: 'Review your saved decisions and resolve any remaining launch requirements.',
  },
];
const INTRODUCTION_KEY = 'helios:company-setup:introduced:v1';

function introductionCompleted() {
  try {
    return window.sessionStorage.getItem(INTRODUCTION_KEY) === '1';
  } catch {
    return false;
  }
}

export function CompanySetupPage() {
  useGlassSpotlight();
  const auth = useAuth();
  const [invitationEntry, setInvitationEntry] = useState(() =>
    readOwnerInvitation(window.location.hash),
  );
  const [introduced, setIntroduced] = useState(
    () => Boolean(readOwnerInvitation(window.location.hash)) || introductionCompleted(),
  );
  useEffect(() => {
    if (invitationEntry && window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
    }
  }, [invitationEntry]);
  const begin = () => {
    setIntroduced(true);
    try {
      // Only a visual preference survives the sign-in route, never company data.
      window.sessionStorage.setItem(INTRODUCTION_KEY, '1');
    } catch {
      // Optional browser storage must never block invitation acceptance.
    }
  };
  const scope = JSON.stringify([
    auth.authenticated,
    auth.user?.id,
    auth.tenant?.id,
    auth.sessionId,
    auth.authVersion,
    [...auth.permissions].sort(),
  ]);
  if (invitationEntry && auth.authenticated)
    return (
      <main className="company-setup-entry">
        <h1>Company owner invitation</h1>
        <p>
          This invitation is for {invitationEntry.email}. Sign out to activate it as the invited
          owner, or continue to your current workspace.
        </p>
        <Button onClick={() => void auth.signOut()}>Sign out to activate invitation</Button>
        <Button variant="outline" onClick={() => setInvitationEntry(null)}>
          Continue with current account
        </Button>
      </main>
    );
  return (
    <CompanySetupPageContent
      key={scope}
      introduced={introduced}
      onBegin={begin}
      invitationEntry={invitationEntry}
      onInvitationAccepted={() => {
        setInvitationEntry(null);
        begin();
      }}
    />
  );
}

function CompanySetupPageContent({
  introduced,
  onBegin,
  invitationEntry,
  onInvitationAccepted,
}: {
  introduced: boolean;
  onBegin: () => void;
  invitationEntry: OwnerInvitationEntry | null;
  onInvitationAccepted: () => void;
}) {
  const auth = useAuth();
  const { setupId } = useParams();
  const [summaries, setSummaries] = useState<SetupSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [replacement, setReplacement] = useState<SetupInvitation | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!auth.authenticated || setupId || !introduced) return;
    let current = true;
    setLoading(true);
    companySetupClient
      .list()
      .then((rows) => {
        if (!current) return;
        setSummaries(rows);
        if (auth.accessMode === 'COMPANY_SETUP' && rows.length === 1)
          navigate(`/company-setup/${rows[0]!.id}`, { replace: true });
      })
      .catch((e: unknown) => {
        if (current) setError(e instanceof Error ? e.message : 'Unable to load company setup');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [
    auth.authenticated,
    auth.accessMode,
    auth.user?.id,
    auth.tenant?.id,
    navigate,
    setupId,
    introduced,
  ]);
  if (auth.isLoading)
    return (
      <main className="company-setup-loading">
        <Spinner />
      </main>
    );
  if (!setupId && !introduced)
    return <CinematicWelcome verified={auth.authenticated} onBegin={onBegin} />;
  if (!auth.authenticated)
    return <SetupEnrollment invitationEntry={invitationEntry} onAccepted={onInvitationAccepted} />;
  if (setupId)
    return <SetupWorkspace key={`${auth.user?.id}:${auth.tenant?.id}:${setupId}`} id={setupId} />;
  return (
    <div className="company-setup company-setup--directory">
      <header className="company-setup-header">
        <SetupBrand />
        <Link to="/">Back to platform</Link>
      </header>
      <main className="company-setup-entry">
        <h1>Set up your company</h1>
        <p>
          Build a separate company workspace with a verified owner, reviewed access, and a clear
          launch plan.
        </p>
        {error && (
          <p role="alert" className="company-setup-error">
            {error}
          </p>
        )}
        {loading && <Spinner />}
        {replacement && (
          <section className="company-setup-handoff">
            <h2>Replacement owner invitation</h2>
            <p>
              The prior code is revoked. No email was sent; hand this code only to the verified
              owner through an approved secure channel.
            </p>
            <code>{replacement.invitationCode}</code>
            <p>Expires {new Date(replacement.expiresAt).toLocaleString()}.</p>
            <Button variant="outline" onClick={() => setReplacement(null)}>
              Close credential display
            </Button>
          </section>
        )}
        {summaries.length > 0 && (
          <section>
            <h2>Your company setups</h2>
            <div className="company-setup-list">
              {summaries.map((row) => (
                <div key={row.id}>
                  <Link to={`/company-setup/${row.id}`}>
                    <strong>{row.name}</strong>
                    <span>
                      {row.status === 'ACTIVE'
                        ? 'Active company'
                        : row.invitationAccepted
                          ? 'Resume setup'
                          : 'Waiting for owner'}{' '}
                      · {row.ownerEmail}
                    </span>
                    <ArrowRight size={18} />
                  </Link>
                  {hasPermission(auth.permissions, 'platform:admin') &&
                    !row.invitationAccepted &&
                    row.status === 'DRAFT' && (
                      <Button
                        variant="outline"
                        disabled={loading}
                        onClick={() => {
                          setLoading(true);
                          setError(null);
                          void companySetupClient
                            .reissue(row.id, row.version)
                            .then((result) => {
                              setReplacement(result);
                              setSummaries((current) =>
                                current.map((item) =>
                                  item.id === row.id
                                    ? { ...item, version: item.version + 1 }
                                    : item,
                                ),
                              );
                            })
                            .catch((e: unknown) =>
                              setError(
                                e instanceof Error ? e.message : 'Unable to reissue invitation',
                              ),
                            )
                            .finally(() => setLoading(false));
                        }}
                      >
                        Reissue owner invitation
                      </Button>
                    )}
                </div>
              ))}
            </div>
          </section>
        )}
        {hasPermission(auth.permissions, 'platform:admin') ? (
          <SetupCreate onCreated={() => undefined} />
        ) : (
          <p>
            A platform administrator issues company-owner invitations after verifying authority. Use
            the invitation entry when you are ready.
          </p>
        )}
      </main>
    </div>
  );
}

function SetupEnrollment({
  invitationEntry,
  onAccepted,
}: {
  invitationEntry: OwnerInvitationEntry | null;
  onAccepted: () => void;
}) {
  const auth = useAuth();
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState(invitationEntry?.email ?? '');
  const [code, setCode] = useState(invitationEntry?.code ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const origin = getSessionOriginKey();
    try {
      const result = await companySetupClient.accept({
        email,
        invitationCode: code.trim(),
        password,
      });
      if (getSessionOriginKey() !== origin) return;
      setCode('');
      // Acceptance consumes the link. Clear it before sign-in publishes the owner
      // session so that session is not mistaken for an unrelated existing account.
      onAccepted();
      const session = await auth.signIn({
        email: result.email,
        password,
        tenantSlug: result.tenantSlug,
      });
      setPassword('');
      if ('requiresWorkspaceSelection' in session || !session.authenticated)
        setMessage(
          'Invitation accepted. Sign in with the password you just set to resume company setup.',
        );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Unable to verify the invitation');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="company-setup company-setup--welcome">
      <header className="company-setup-header">
        <SetupBrand />
        <span className="setup-header-caption">OWNER ENROLLMENT</span>
        <Link to="/auth">
          <ArrowLeft size={16} /> Back to sign in
        </Link>
      </header>
      <main className="company-setup-entry company-setup-enrollment">
        <motion.div
          className="setup-welcome-story"
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.65 }}
        >
          <span className="setup-eyebrow">
            <span /> WORKSPACE ACCESS
          </span>
          <h1>
            Set up <span>your company</span>
          </h1>
          <p className="setup-welcome-description">
            Verify your owner invitation, then configure your company, people and data sources.
          </p>
          <SetupBlueprint />
          <ol className="company-setup-intro" aria-label="Getting started">
            <li>
              <span>01</span>
              <div>
                <strong>Verify your invitation</strong>
                <small>Start with your reviewed owner invitation.</small>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>Make it yours</strong>
                <small>Set up your company, people, and connections.</small>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>Review. Then launch.</strong>
                <small>You approve the details before going live.</small>
              </div>
            </li>
          </ol>
          <Link
            className="company-setup-text-link"
            to="/auth"
            state={{ from: { pathname: '/company-setup' } }}
          >
            Sign in to manage or resume setup <ArrowRight size={16} />
          </Link>
        </motion.div>
        <motion.form
          onSubmit={(event) => void submit(event)}
          className="company-setup-invite-form glass"
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.65, delay: reduceMotion ? 0 : 0.12 }}
        >
          <fieldset disabled={busy} className="company-setup-controls">
            <div className="setup-invite-heading">
              <span className="setup-invite-icon">
                <ShieldCheck size={24} />
              </span>
              <span className="setup-eyebrow">
                OWNER ACCESS <span className="setup-step-count">01 / 03</span>
              </span>
              <h2>Accept your owner invitation</h2>
              <p>Use your invited email and one-time code to verify owner access.</p>
            </div>
            <SetupField label="Invited email">
              <Input
                type="email"
                autoComplete="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </SetupField>
            <SetupField
              label="One-time invitation code"
              hint="Issued by your platform administrator through a secure channel."
            >
              <Input
                autoComplete="off"
                required
                minLength={64}
                maxLength={64}
                placeholder="Paste your invitation code"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </SetupField>
            <SetupField
              label="Create a password"
              hint="Use 15–128 characters. Microsoft workspace consent is separate from app authentication."
            >
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={15}
                maxLength={128}
                placeholder="At least 15 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </SetupField>
            {message && (
              <p role="alert" className="company-setup-error">
                {message}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {busy ? (
                <>
                  <Spinner /> Verifying invitation…
                </>
              ) : (
                <>
                  Verify invitation and continue <ArrowRight size={17} />
                </>
              )}
            </Button>
          </fieldset>
          <div className="setup-invite-footnote">
            <LockKeyhole size={14} />
            <span>Your invitation is personal and can only be used once.</span>
          </div>
        </motion.form>
        <div className="setup-welcome-footer">
          <span>
            <ShieldCheck size={15} /> Your workspace. Your approval.
          </span>
          <p>
            An email domain or Microsoft connection never claims a company. Existing accounts can
            sign in to resume. Password and passkey sign-in remain available.
          </p>
        </div>
      </main>
    </div>
  );
}

function SetupCreate({ onCreated }: { onCreated: () => void }) {
  const [values, setValues] = useState({
    legalName: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    licensedSeats: 1,
    ownerVerificationNote: '',
    ownerAuthorityConfirmed: false,
  });
  const [invitation, setInvitation] = useState<
    (SetupInvitation & { ownerName: string; ownerEmail: string }) | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const submitted = { ...values };
      const result = await companySetupClient.create(submitted);
      setInvitation({
        ...result,
        ownerName: submitted.ownerName,
        ownerEmail: submitted.ownerEmail,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to create reviewed setup');
    } finally {
      setBusy(false);
    }
  };
  if (invitation)
    return (
      <section className="company-setup-handoff">
        <h2>Owner invitation created</h2>
        <p>
          Verified owner: {invitation.ownerName} ({invitation.ownerEmail})
        </p>
        <p>
          No email was sent. Give the one-time code only to the verified owner through an approved
          secure channel. It is not shown again after leaving this screen.
        </p>
        <code>{invitation.invitationCode}</code>
        <p>Expires {new Date(invitation.expiresAt).toLocaleString()}.</p>
        <Link to={`/company-setup/${invitation.id}`}>Review this draft company</Link>
        <Button variant="outline" onClick={() => setInvitation(null)}>
          Close credential display
        </Button>
      </section>
    );
  return (
    <section>
      <h2>Review a new company enrollment</h2>
      <p>
        Platform administration only. Verify the owner independently before creating this
        invitation.
      </p>
      <form onSubmit={(e) => void submit(e)}>
        <fieldset disabled={busy} className="company-setup-controls company-setup-form-grid">
          <SetupField label="Legal company name">
            <Input
              required
              value={values.legalName}
              onChange={(e) => setValues({ ...values, legalName: e.target.value })}
            />
          </SetupField>
          <SetupField
            label="Workspace address"
            hint="A unique permanent address, such as acme-contracting."
          >
            <Input
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              minLength={3}
              value={values.slug}
              onChange={(e) => setValues({ ...values, slug: e.target.value })}
            />
          </SetupField>
          <SetupField label="Verified owner name">
            <Input
              required
              value={values.ownerName}
              onChange={(e) => setValues({ ...values, ownerName: e.target.value })}
            />
          </SetupField>
          <SetupField label="Verified owner email">
            <Input
              required
              type="email"
              value={values.ownerEmail}
              onChange={(e) => setValues({ ...values, ownerEmail: e.target.value })}
            />
          </SetupField>
          <SetupField
            label="Reviewed app-user seat allowance"
            hint="Includes the owner. This records an access allowance; it creates no subscription or charge."
          >
            <Input
              type="number"
              required
              min={1}
              max={500}
              value={values.licensedSeats}
              onChange={(e) => setValues({ ...values, licensedSeats: Number(e.target.value) })}
            />
          </SetupField>
          <SetupField
            label="Owner authority verification"
            hint="Record how the named person's authority was verified; do not paste identity documents or secrets."
          >
            <textarea
              required
              minLength={15}
              maxLength={1000}
              value={values.ownerVerificationNote}
              onChange={(e) => setValues({ ...values, ownerVerificationNote: e.target.value })}
            />
          </SetupField>
          <div className="company-setup-full">
            <SetupCheck
              checked={values.ownerAuthorityConfirmed}
              onChange={(v) => setValues({ ...values, ownerAuthorityConfirmed: v })}
            >
              I independently verified this owner’s authority and reviewed the company seat
              allowance.
            </SetupCheck>
          </div>
          {error && (
            <p role="alert" className="company-setup-error company-setup-full">
              {error}
            </p>
          )}
          <Button type="submit" disabled={busy || !values.ownerAuthorityConfirmed}>
            {busy ? <Spinner /> : 'Create reviewed owner invitation'}
          </Button>
        </fieldset>
      </form>
    </section>
  );
}

function SetupWorkspace({ id }: { id: string }) {
  const auth = useAuth();
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const step = Math.min(4, Math.max(0, Math.trunc(Number(params.get('step')) || 0)));
  const mainBody = useRef<HTMLDivElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (previousStep.current === step) return;
    previousStep.current = step;
    if (!mainBody.current) return;
    mainBody.current.scrollTop = 0;
    if (window.matchMedia('(max-width: 1023px)').matches)
      mainBody.current.scrollIntoView?.({ block: 'start', behavior: 'instant' });
  }, [step]);
  const [view, setView] = useState<CompanySetupView | null>(null);
  const [draft, setDraft] = useState<SetupDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const acceptView = useCallback((next: CompanySetupView) => {
    setView(next);
    setDraft(draftFrom(next));
  }, []);
  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const next = await companySetupClient.get(id);
      if (mounted.current) {
        acceptView(next);
        setError(null);
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : 'Unable to load setup');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [acceptView, id]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
  }, [refresh]);
  const dirty = Boolean(view && draft && JSON.stringify(draft) !== JSON.stringify(draftFrom(view)));
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (e) {
      if (mounted.current)
        setError(e instanceof Error ? e.message : 'The action could not be completed');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const save = async (nextStep?: number) => {
    if (!view || !draft) return;
    await run(async () => {
      const next = await companySetupClient.save(id, view.version, draft);
      if (!mounted.current) return;
      acceptView(next);
      setNotice('Progress saved.');
      if (nextStep !== undefined) setParams({ step: String(nextStep) });
    });
  };
  if (!view || !draft)
    return (
      <div className="company-setup">
        <header className="company-setup-header">
          <SetupBrand />
          <Link to="/company-setup">Company setups</Link>
        </header>
        <main className="company-setup-entry">
          {error ? (
            <>
              <p role="alert">{error}</p>
              <Button onClick={() => void refresh()}>Retry</Button>
            </>
          ) : (
            <Spinner />
          )}
        </main>
      </div>
    );
  const active = view.status === 'ACTIVE';
  const canConnect = auth.tenant?.id === view.tenantId;
  const update = (patch: Partial<SetupDraft>) => setDraft({ ...draft, ...patch });
  return (
    <div className="company-setup company-setup--workspace">
      <header className="company-setup-header">
        <SetupBrand />
        <div>
          <span className="company-setup-muted">
            <span
              className={
                dirty ? 'setup-save-indicator setup-save-indicator--dirty' : 'setup-save-indicator'
              }
            />
            {dirty ? 'Unsaved changes' : 'Progress saved'}
          </span>
          {!active && (
            <Button variant="outline" disabled={busy} onClick={() => void save()}>
              Save progress
            </Button>
          )}
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const origin = getSessionOriginKey();
                if (dirty) {
                  acceptView(await companySetupClient.save(id, view.version, draft));
                }
                if (!mounted.current || !isCurrentSessionOrigin(origin)) return;
                await auth.signOut();
                navigate('/auth');
              })
            }
          >
            <LogOut size={15} /> Save and exit
          </Button>
        </div>
      </header>
      <div className="company-setup-layout">
        <nav aria-label="Company setup steps" className="company-setup-steps">
          <div className="setup-rail-heading">
            <span className="setup-eyebrow">BUILD YOUR WORKSPACE</span>
            <strong>{view.profile.workspaceName || view.profile.legalName}</strong>
            <span className="setup-rail-position">
              Step {String(step + 1).padStart(2, '0')} <span>of 05</span>
            </span>
            <div
              className="setup-position-track"
              role="progressbar"
              aria-label="Current setup step"
              aria-valuemin={1}
              aria-valuemax={5}
              aria-valuenow={step + 1}
              aria-valuetext={`Step ${step + 1} of 5: ${STEPS[step]}`}
            >
              <span style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>
          <ol>
            {STEPS.map((label, index) => {
              const Icon = STEP_DETAILS[index]!.icon;
              return (
                <li key={label}>
                  <button
                    disabled={busy}
                    aria-label={label}
                    aria-current={step === index ? 'step' : undefined}
                    onClick={() => setParams({ step: String(index) })}
                  >
                    <span className="setup-nav-icon">
                      <Icon size={19} />
                    </span>
                    <span className="setup-nav-copy">
                      <small>0{index + 1}</small>
                      <strong>{label}</strong>
                      <span>{STEP_DETAILS[index]!.detail}</span>
                    </span>
                    {step === index && <span className="setup-nav-current" />}
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="setup-rail-bottom">
            <SetupBlueprint compact />
            <p>
              <FileText size={16} />
              {active ? 'Active workspace' : 'Draft workspace'}
            </p>
            <small>
              {active
                ? 'Manage your company in the platform.'
                : 'Take it at your pace. Save and return anytime.'}
            </small>
          </div>
        </nav>
        <main className="company-setup-main" aria-busy={busy}>
          <div ref={mainBody} className="company-setup-main-body">
            <span className="setup-eyebrow">
              {active ? 'WORKSPACE ACTIVATED' : `STEP 0${step + 1} / ${STEPS[step]?.toUpperCase()}`}
            </span>
            <h1>{active ? 'Your company is ready' : 'Set up your company'}</h1>
            <p className="company-setup-lead">
              {active
                ? 'Continue onboarding your team and manage connections in the platform.'
                : STEP_DETAILS[step]!.description}
            </p>
            {error && (
              <p role="alert" className="company-setup-error">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="company-setup-notice">
                {notice}
              </p>
            )}
            <motion.div
              key={active ? 'active' : step}
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}
            >
              <fieldset disabled={busy} className="company-setup-controls">
                {active ? (
                  <SetupLaunch
                    view={view}
                    busy={busy}
                    canActivate={false}
                    onActivate={() => undefined}
                    onRun={run}
                  />
                ) : (
                  <>
                    {step === 0 && <SetupCompanyForm view={view} draft={draft} update={update} />}
                    {step === 1 && <SetupPeople view={view} draft={draft} update={update} />}
                    {step === 2 && (
                      <SetupConnections
                        view={view}
                        draft={draft}
                        update={update}
                        dirty={dirty}
                        canConnect={canConnect}
                        busy={busy}
                        onRun={run}
                        onRefresh={refresh}
                      />
                    )}
                    {step === 3 && <SetupImportReview view={view} draft={draft} update={update} />}
                    {step === 4 && (
                      <SetupLaunch
                        view={view}
                        busy={busy}
                        canActivate={!dirty && view.blockers.length === 0 && canConnect}
                        onRun={run}
                        onActivate={() =>
                          void run(async () => {
                            const origin = getSessionOriginKey();
                            const result = await companySetupClient.activate(id, view.version);
                            if (!mounted.current || !isCurrentSessionOrigin(origin)) return;
                            setNotice(result.detail);
                            await auth.signOut();
                            navigate('/auth', { state: { from: { pathname: '/company-setup' } } });
                          })
                        }
                      />
                    )}
                  </>
                )}
              </fieldset>
            </motion.div>
          </div>
          <footer className="company-setup-footer">
            <Link to={active ? '/' : '/company-setup'}>
              <ArrowLeft size={17} />
              {active ? 'Enter platform' : 'Company setups'}
            </Link>
            {!active && (
              <Button disabled={busy} onClick={() => void save(Math.min(4, step + 1))}>
                {busy ? (
                  <Spinner />
                ) : (
                  <>
                    {step === 4 ? 'Save review' : 'Save and continue'}
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            )}
          </footer>
        </main>
        <SetupAssistant
          view={view}
          draft={draft}
          dirty={dirty}
          busy={busy}
          stepLabel={STEPS[step]!}
          onUpdate={update}
          onRun={run}
          onRefresh={refresh}
          onAcceptView={acceptView}
        />
      </div>
    </div>
  );
}
